import { useQuery } from '@tanstack/react-query';
import { graphql } from '../gql/gql';
import { graphqlRequest, shouldRetryQuery } from '../utils/graphqlClient';
import {
  GetMainRouteLandingDataQuery,
  GetRouteLandingDataQuery,
} from '../gql/graphql';

const getMainRouteLandingData = graphql(/* GraphQL */ `
  query getMainRouteLandingData($mainRouteId: Int, $slug: String) {
    mainRouteLandingData(mainRouteId: $mainRouteId, slug: $slug) {
      mainRouteId
      mainRouteName
      countryId
      countryName
      routeImageUrl
      minPrice
      maxPrice
      currency
      departureCount
      nextDepartureDate
      boats {
        boatId
        name
        slug
        currency
        minPrice
        heroImageUrl
      }
    }
  }
`);

const getRouteLandingDataFallback = graphql(/* GraphQL */ `
  query getRouteLandingDataFallback($slug: String!) {
    routeLandingData(slug: $slug) {
      routeId
      routeName
      countryId
      countryName
      routeImageUrl
      minPrice
      maxPrice
      currency
      departureCount
      nextDepartureDate
      boats {
        boatId
        name
        slug
        currency
        minPrice
        heroImageUrl
      }
    }
  }
`);

type MainRouteLandingResult =
  GetMainRouteLandingDataQuery['mainRouteLandingData'];

async function fetchMainRouteLandingData(
  mainRouteId?: number,
  slug?: string,
): Promise<MainRouteLandingResult> {
  if (!mainRouteId && !slug) {
    return null;
  }

  try {
    const primary = await graphqlRequest<GetMainRouteLandingDataQuery>(
      getMainRouteLandingData,
      {
        mainRouteId: mainRouteId ?? null,
        slug: slug ?? null,
      },
    );

    if (primary.mainRouteLandingData) {
      return primary.mainRouteLandingData;
    }
  } catch {
    // Older API builds may not expose mainRouteLandingData yet.
  }

  if (!slug) {
    return null;
  }

  const fallback = await graphqlRequest<GetRouteLandingDataQuery>(
    getRouteLandingDataFallback,
    { slug },
  );

  const route = fallback.routeLandingData;
  if (!route) {
    return null;
  }

  return {
    __typename: 'MainRouteLandingData',
    mainRouteId: mainRouteId ?? route.routeId,
    mainRouteName: route.routeName,
    countryId: route.countryId,
    countryName: route.countryName,
    routeImageUrl: route.routeImageUrl,
    minPrice: route.minPrice,
    maxPrice: route.maxPrice,
    currency: route.currency,
    departureCount: route.departureCount,
    nextDepartureDate: route.nextDepartureDate,
    boats: route.boats,
  };
}

const useGetMainRouteLandingData = (
  params: { mainRouteId?: number; slug?: string },
  enabled = true,
) => {
  const { mainRouteId, slug } = params;
  const hasIdentifier = Boolean(mainRouteId || slug);

  const { data, isLoading } = useQuery<MainRouteLandingResult>({
    queryKey: ['mainRouteLandingData', mainRouteId ?? null, slug ?? null],
    queryFn: () => fetchMainRouteLandingData(mainRouteId, slug),
    enabled: enabled && hasIdentifier,
    retry: shouldRetryQuery,
    staleTime: 5 * 60 * 1000,
  });

  return {
    mainRouteLandingData: data ?? null,
    mainRouteLandingLoading: isLoading,
  };
};

export default useGetMainRouteLandingData;
